/**
 * Governments, revolutions and war weariness (ADR-0059).
 *
 * A government is a set of numbers in governments.json that the rest of the engine
 * reads through this module: the science cap, how much distance costs, what rich land
 * yields, how many units the cities carry for free, how content citizens are, and how
 * much the people mind losing a war. Changing government passes through a short
 * anarchy first — the price that makes the choice a decision.
 *
 * Nothing here imports the cities or the economy; they import this. So any of them can
 * ask "what does this civilisation's government say?" without a cycle.
 */

import { traitSum } from '../civs/traits.js';
import type { Content, GovernmentDef } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { hasTech } from '../research/knowledge.js';
import { NONE, type Civ, type GameState, type Id } from '../state/types.js';

export type GovernmentEffects = GovernmentDef['effects'];

/** The government a civilisation lives under now (anarchy during a revolution). */
export function governmentOf(content: Content, civ: Civ | undefined): GovernmentDef {
  const fallback = content.governments.byKey.get(content.rules.government.start)!;
  if (!civ || civ.government < 0 || civ.government >= content.governments.all.length) return fallback;
  return content.governments.get(civ.government);
}

export function governmentEffects(state: GameState, content: Content, civId: Id): GovernmentEffects {
  return governmentOf(content, state.civs[civId]).effects;
}

/** The highest science rate this civilisation's government allows. */
export function maxScienceRate(state: GameState, content: Content, civId: Id): number {
  return Math.min(content.rules.research.maxRate, governmentEffects(state, content, civId).maxScienceRate);
}

export function inAnarchy(civ: Civ | undefined): boolean {
  return !!civ && civ.nextGovernment !== NONE;
}

/** Turns of anarchy still to play, this one included, or 0. */
export function anarchyLeft(state: GameState, civ: Civ): number {
  return civ.nextGovernment === NONE ? 0 : Math.max(0, civ.anarchyEnds - state.meta.turn);
}

/** Governments the civilisation could choose now: selectable, and its technology known. */
export function availableGovernments(content: Content, civ: Civ): GovernmentDef[] {
  return content.governments.all.filter((g) => g.selectable && hasTech(civ, g.techId));
}

export type ChangeRefusal = 'NoSuchGovernment' | 'NeedsTech' | 'AlreadyGovernment' | 'InRevolution';

/** Why a civilisation cannot change to this government, or null. */
export function changeBlocker(content: Content, civ: Civ, target: number): ChangeRefusal | null {
  if (!Number.isInteger(target) || target < 0 || target >= content.governments.all.length) return 'NoSuchGovernment';
  const def = content.governments.get(target);
  if (!def.selectable) return 'NoSuchGovernment';
  if (inAnarchy(civ)) return 'InRevolution';
  if (civ.government === target) return 'AlreadyGovernment';
  if (!hasTech(civ, def.techId)) return 'NeedsTech';
  return null;
}

/** Begin a revolution (or, with no anarchy configured, change at once). */
export function startRevolution(state: GameState, content: Content, civ: Civ, target: number): GameEvent[] {
  // A religious people's revolutions are shorter (ADR-0062).
  const turns = Math.max(0, content.rules.government.anarchyTurns - traitSum(content, civ, 'anarchyTurnsLess'));
  const name = content.governments.get(target).name;
  if (turns <= 0) return [...install(state, content, civ, target)];
  civ.government = content.governments.id(content.rules.government.anarchy);
  civ.nextGovernment = target;
  // Declared during turn T: turns T … T+turns-1 are played in anarchy, and their
  // turn-ends are worked under it; the new government arrives as turn T+turns begins.
  civ.anarchyEnds = state.meta.turn + turns;
  civ.governmentSince = state.meta.turn;
  return [{ t: 'RevolutionStarted', civId: civ.id, target, name, turns }];
}

function install(state: GameState, content: Content, civ: Civ, target: number): GameEvent[] {
  civ.government = target;
  civ.nextGovernment = NONE;
  civ.anarchyEnds = 0;
  civ.governmentSince = state.meta.turn;
  // The slider shows what the new government allows; nothing is spent above its cap.
  civ.researchRate = Math.min(civ.researchRate, maxScienceRate(state, content, civ.id));
  return [{ t: 'GovernmentChanged', civId: civ.id, government: target, name: content.governments.get(target).name }];
}

/** Whether a civilisation is at war with anyone. */
function atWarWithAnyone(state: GameState, civId: Id): boolean {
  return state.relations.some((r) => r.war && (r.a === civId || r.b === civId));
}

/**
 * A civilisation's turn-end, after its cities have worked: a revolution that has run its
 * course installs the new government — so the last anarchic turn is worked as anarchy
 * and the new government is in place for the turn the player plays next — and war
 * weariness fades, slowly at war and quickly at peace.
 */
export function advanceGovernment(state: GameState, content: Content, civId: Id): GameEvent[] {
  const civ = state.civs[civId];
  if (!civ) return [];
  const events: GameEvent[] = [];
  if (civ.nextGovernment !== NONE && state.meta.turn >= civ.anarchyEnds) {
    events.push(...install(state, content, civ, civ.nextGovernment));
  }
  if (civ.weariness > 0) {
    const rules = content.rules.weariness;
    const decay = atWarWithAnyone(state, civId) ? rules.decayAtWar : rules.decayAtPeace;
    civ.weariness = Math.max(0, Math.floor(civ.weariness * (1 - decay)));
  }
  return events;
}

/** A loss at war: the people remember it (only the loser grows weary). */
export function addWeariness(state: GameState, civId: Id, shields: number): void {
  const civ = state.civs[civId];
  if (civ && shields > 0) civ.weariness += shields;
}

/** Discontented citizens war weariness adds in each of this civilisation's cities. */
export function wearyCitizens(state: GameState, content: Content, civId: Id): number {
  const civ = state.civs[civId];
  if (!civ || civ.weariness <= 0) return 0;
  const rules = content.rules.weariness;
  const scaled = (civ.weariness * governmentEffects(state, content, civId).warWeariness) / rules.shieldsPerCitizen;
  return Math.min(rules.maxCitizens, Math.floor(scaled));
}

/** A tile's yield as this government lets it be worked. */
export function governedYield<T extends { food: number; production: number; gold: number }>(effects: GovernmentEffects, y: T): T {
  if (!effects.tilePenalty && effects.goldTileBonus === 0) return y;
  const cut = (v: number): number => (effects.tilePenalty && v > 2 ? v - 1 : v);
  return {
    ...y,
    food: cut(y.food),
    production: cut(y.production),
    gold: cut(y.gold) + (y.gold >= effects.goldTileMinimum ? effects.goldTileBonus : 0),
  };
}

/** How much an AI civilisation wants a government right now (ADR-0059). */
export function governmentAppeal(state: GameState, content: Content, civ: Civ, def: GovernmentDef, warPenalty: number): number {
  const personality = civ.personality >= 0 ? content.personalities.get(civ.personality).key : '';
  const base = def.aiValue[personality] ?? def.aiValue['base'] ?? 0;
  return base - (atWarWithAnyone(state, civ.id) ? def.effects.warWeariness * warPenalty : 0);
}
